import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';

const COLORS = [
  '#4d5ef7',
  '#00bbd4',
  '#f89f00',
  '#fa3941',
  '#727df9',
  '#00cce8',
  '#fbe63f',
  '#f06568',
];

interface ChartSeries {
  key: string;
  label?: string;
  color?: string;
}

interface ChartSpec {
  type: 'bar' | 'line' | 'pie' | 'area';
  title?: string;
  data: Record<string, unknown>[];
  xKey?: string;
  series?: ChartSeries[];
}

function parseSpec(json: string): ChartSpec {
  const spec = JSON.parse(json);
  if (!spec.type || !Array.isArray(spec.data)) {
    throw new Error('Invalid chart spec: missing type or data');
  }
  return spec;
}

function detectSeries(data: Record<string, unknown>[], xKey: string): ChartSeries[] {
  if (!data.length) return [];
  const keys = Object.keys(data[0]).filter((k) => k !== xKey && typeof data[0][k] === 'number');
  return keys.map((key, i) => ({ key, label: key, color: COLORS[i % COLORS.length] }));
}

function aggregatePieData(data: Record<string, unknown>[], nameKey: string, valueKey: string) {
  if (data.length <= 8) return data;
  const sorted = [...data].sort((a, b) => (b[valueKey] as number) - (a[valueKey] as number));
  const top7 = sorted.slice(0, 7);
  const otherValue = sorted.slice(7).reduce((sum, d) => sum + (d[valueKey] as number), 0);
  return [...top7, { [nameKey]: 'Other', [valueKey]: otherValue }];
}

interface KitChartProps {
  json: string;
}

export function KitChart({ json }: KitChartProps) {
  const [modalOpen, setModalOpen] = useState(false);

  let spec: ChartSpec;
  try {
    spec = parseSpec(json);
  } catch {
    return (
      <Box sx={{ p: 1.5, color: 'var(--grey-gr-500, #6B7280)', fontSize: '0.75rem' }}>
        Unable to render chart: invalid data.
      </Box>
    );
  }

  if (!spec.data.length) {
    return (
      <Box sx={{ p: 1.5, color: 'var(--grey-gr-500, #6B7280)', fontSize: '0.75rem' }}>
        No data to display.
      </Box>
    );
  }

  const validTypes = ['bar', 'line', 'pie', 'area'];
  if (!validTypes.includes(spec.type)) {
    return (
      <Box sx={{ p: 1.5, color: 'var(--grey-gr-500, #6B7280)', fontSize: '0.75rem' }}>
        Unsupported chart type: {spec.type}
      </Box>
    );
  }

  const xKey = spec.xKey || 'name';
  const series =
    spec.type === 'pie' ? [] : spec.series?.length ? spec.series : detectSeries(spec.data, xKey);

  const isLarge = spec.data.length > 8 || series.length > 2;

  const renderChart = (height: number) => {
    const tickAngle = spec.data.length > 6 ? -45 : 0;
    const tickAnchor = spec.data.length > 6 ? 'end' : 'middle';
    const bottomMargin = spec.data.length > 6 ? 60 : 20;

    if (spec.type === 'pie') {
      const numericKey =
        Object.keys(spec.data[0]).find((k) => k !== xKey && typeof spec.data[0][k] === 'number') ||
        'value';
      const pieData = aggregatePieData(spec.data, xKey, numericKey);

      return (
        <ResponsiveContainer width='100%' height={height}>
          <PieChart>
            <Pie
              data={pieData}
              dataKey={numericKey}
              nameKey={xKey}
              cx='50%'
              cy='50%'
              outerRadius={height * 0.35}
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              labelLine={false}
              fontSize={11}
            >
              {pieData.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    const ChartComponent =
      spec.type === 'bar' ? BarChart : spec.type === 'line' ? LineChart : AreaChart;
    const SeriesComponent = spec.type === 'bar' ? Bar : spec.type === 'line' ? Line : Area;

    return (
      <ResponsiveContainer width='100%' height={height}>
        <ChartComponent
          data={spec.data}
          margin={{ top: 5, right: 20, left: 0, bottom: bottomMargin }}
        >
          <CartesianGrid strokeDasharray='3 3' stroke='#E5E7EB' />
          <XAxis dataKey={xKey} tick={{ fontSize: 11 }} angle={tickAngle} textAnchor={tickAnchor} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip contentStyle={{ fontSize: 12 }} />
          {series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
          {series.map((s, i) => (
            <SeriesComponent
              key={s.key}
              dataKey={s.key}
              name={s.label || s.key}
              fill={s.color || COLORS[i % COLORS.length]}
              stroke={s.color || COLORS[i % COLORS.length]}
              {...(spec.type === 'area' ? { fillOpacity: 0.3 } : {})}
            />
          ))}
        </ChartComponent>
      </ResponsiveContainer>
    );
  };

  const chartModal = (
    <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth={false}>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.875rem',
          fontWeight: 600,
          pr: 1,
        }}
      >
        {spec.title || 'Chart'}
        <IconButton size='small' onClick={() => setModalOpen(false)}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ width: 700, maxWidth: '90vw' }}>{renderChart(400)}</DialogContent>
    </Dialog>
  );

  // Large charts: show compact placeholder with button
  if (isLarge) {
    return (
      <Box sx={{ my: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.25,
            borderRadius: '8px',
            border: '1px solid var(--grey-gr-200, #E5E7EB)',
            backgroundColor: 'var(--grey-gr-50, #F9FAFB)',
          }}
        >
          <Typography
            sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8125rem', fontWeight: 500 }}
          >
            {spec.title || 'Chart'}
          </Typography>
          <Button
            size='small'
            variant='outlined'
            onClick={() => setModalOpen(true)}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.7rem',
              textTransform: 'none',
              borderColor: 'var(--neon-blue-nb-400, #4d5ef7)',
              color: 'var(--neon-blue-nb-400, #4d5ef7)',
            }}
          >
            View chart
          </Button>
        </Box>
        {chartModal}
      </Box>
    );
  }

  // Small charts: render inline with expand button
  return (
    <Box sx={{ my: 1, position: 'relative' }}>
      {spec.title && (
        <Typography
          sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}
        >
          {spec.title}
        </Typography>
      )}
      <IconButton
        size='small'
        onClick={() => setModalOpen(true)}
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          zIndex: 1,
          opacity: 0.6,
          '&:hover': { opacity: 1 },
        }}
      >
        <OpenInFullIcon sx={{ fontSize: 14 }} />
      </IconButton>
      {renderChart(220)}
      {chartModal}
    </Box>
  );
}
